import { Injectable } from '@angular/core';
import { TimeRangeOption } from '@netfoundry-ui/ui/time-range-pill';
import { BehaviorSubject, Observable } from 'rxjs';

/**
 * The windows the Alerts page offers, in hours.
 *
 * <strong>Every one is bounded, and 30 days is the widest.</strong> There is no "all time":
 * an unbounded window makes the page read every alert the network has ever raised, and it
 * left the chart above the table with no left edge - so the two described different spans
 * and the page had to apologise for it in a footnote.
 *
 * These go out as `firstAssertedAfter`, which the service applies to `firstAssertedAt` - the
 * same field the listing sorts on, and the one that is set even while an alert is still
 * pending - where `openedAfter`, the intuitive choice, bounds `openedAt`, which is null
 * until an alert has been held for its `for` duration, and a range never matches a missing
 * field.
 */
const RANGE_HOURS: Record<string, number> = {
    '1h': 1,
    '24h': 24,
    '7d': 24 * 7,
    '30d': 24 * 30,
};

const RANGE_OPTIONS: TimeRangeOption[] = [
    { id: '1h', label: '1h' },
    { id: '24h', label: '24h' },
    { id: '7d', label: '7d' },
    { id: '30d', label: '30d' },
];

/* The widest window there is, so the page still opens on as much as it can show. */
const DEFAULT_RANGE = '30d';

/**
 * How many points to cut each window into, and whether its ticks read a time of day.
 *
 * The bucket count lives here rather than in the component, because this is already the one
 * place that knows what a range means and a second table of window sizes would be two
 * answers free to disagree. The counts are chosen so the width is a round number a reader
 * can name: 12 over an hour is five minutes, 28 over a week is six hours.
 *
 * The window's length is deliberately not repeated here - it comes from {@link RANGE_HOURS}
 * through {@link AlertsTimeRangeService.windowStart}, so a range cannot be one span on the
 * chart and another on the table.
 */
const RANGE_BUCKETS: Record<string, { buckets: number; hourly: boolean }> = {
    '1h': { buckets: 12, hourly: true },
    '24h': { buckets: 24, hourly: true },
    '7d': { buckets: 28, hourly: false },
    '30d': { buckets: 30, hourly: false },
};

/** What the chart shows, and how it is cut up. */
export interface AlertsTimelineWindow {
    /** ISO-8601, inclusive. */
    from: string;
    /** ISO-8601, exclusive. */
    to: string;
    buckets: number;
    /** True when a tick should read a time of day rather than a date. */
    hourly: boolean;
}

/**
 * The one time window the Alerts page is read against.
 *
 * Held in a service because the control and the page it scopes are in different components:
 * the pill is drawn in the app page header, on the line with the tabs, while the counts and
 * the listing it narrows are in the routed page below, and neither is the other's parent.
 * One window for the whole page rather than one per section, because the Overview counts and
 * the table are two readings of the same set of alerts - and root-scoped, so the choice
 * survives a trip to the Mutes tab and back.
 */
@Injectable({ providedIn: 'root' })
export class AlertsTimeRangeService {
    /** The pills to draw, in order. */
    readonly options = RANGE_OPTIONS;
    readonly defaultRange = DEFAULT_RANGE;

    private readonly selected = new BehaviorSubject<string>(DEFAULT_RANGE);

    /** The window now, for a caller that only has to read it once. */
    get range(): string {
        return this.selected.value;
    }

    /** Emits the window on subscribe, and again on every change. */
    get changes(): Observable<string> {
        return this.selected.asObservable();
    }

    /** Silent when the window is already the one asked for, so a redundant click costs no reload. */
    set(range: string): void {
        if (range === this.selected.value) {
            return;
        }
        this.selected.next(range);
    }

    reset(): void {
        this.set(DEFAULT_RANGE);
    }

    /**
     * The instant the window starts, counted back from now. Always an instant, because every
     * window is bounded. The caller keeps the answer — see AlertsComponent.applyRange, which
     * puts it on the filters both endpoints are read with, so the counts and the table can
     * never be bounded at two different instants.
     */
    windowStart(range: string = this.range): string {
        const hours = RANGE_HOURS[range] ?? RANGE_HOURS[DEFAULT_RANGE];
        return new Date(Date.now() - hours * 60 * 60 * 1000).toISOString();
    }

    /**
     * The window to chart, as the timeline endpoint takes it.
     *
     * `to` is now rather than the end of the last whole bucket, so the final bucket is a
     * partial one - which is honest, and is what makes the newest point move as alerts
     * arrive.
     *
     * **Pass the start the caller already put on its filters.** Left to default, this
     * reads the clock a second time, and the chart is then bounded a few milliseconds
     * after the listing beside it - two instants that happen to agree rather than one.
     * Invisible at these bucket widths, but it is the guarantee the page's "one window"
     * rule is written against, so the caller hands over the instant it resolved rather
     * than trusting two reads to match.
     *
     * @param from the already-resolved window start; defaults to reading the clock again
     */
    timelineWindow(range: string = this.range, from: string = this.windowStart(range)): AlertsTimelineWindow {
        const window = RANGE_BUCKETS[range] ?? RANGE_BUCKETS[DEFAULT_RANGE];
        return {
            from,
            to: new Date().toISOString(),
            buckets: window.buckets,
            hourly: window.hourly,
        };
    }

    /** The window's name, for a view that says what it is showing. */
    label(range: string = this.range): string {
        return RANGE_OPTIONS.find((option) => option.id === range)?.label ?? '';
    }
}
